import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users2, DollarSign, Check } from "lucide-react";
import { services } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { createInquiry } from "../lib/api";

export default function WorkWithUs() {
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    const service = services.find((s) => s.id === open)?.title || "";
    try {
      await createInquiry({ ...form, service });
      toast({ title: "Inquiry received!", description: "We'll be in touch within 2 business days." });
      setForm({ name: "", email: "", message: "" });
      setOpen(null);
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    }
  };

  return (
    <div>
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">Work With Us</p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">Two owners in your corner.</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          No frameworks pulled from a textbook. Just real, hard-won experience from two people who've built, scaled,
          and survived nine businesses between them. Here's how we can help.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-10 space-y-8">
        {services.map((s, i) => (
          <div key={s.id} className={`grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden bg-white border lift ${i % 2 ? "md:[direction:rtl]" : ""}`} style={{ borderColor: "var(--cream-dark)" }}>
            <div className="h-64 md:h-auto md:[direction:ltr]">
              <img src={s.img} alt={s.title} className="w-full h-full object-cover min-h-[260px]" />
            </div>
            <div className="p-8 md:p-10 md:[direction:ltr] flex flex-col justify-center">
              <h2 className="font-serif-display text-3xl font-bold text-navy">{s.title}</h2>
              <p className="font-serif-body text-xl text-orange italic mt-1">{s.tagline}</p>
              <p className="font-ui text-navy/75 mt-4 leading-relaxed">{s.desc}</p>

              <div className="grid sm:grid-cols-3 gap-3 mt-6">
                <div className="flex items-start gap-2">
                  <Users2 size={17} className="text-teal shrink-0 mt-0.5" />
                  <div><p className="font-ui text-[11px] text-navy/50 uppercase tracking-wide">Who it's for</p><p className="font-ui text-xs text-navy/80">{s.forWho}</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock size={17} className="text-teal shrink-0 mt-0.5" />
                  <div><p className="font-ui text-[11px] text-navy/50 uppercase tracking-wide">Format</p><p className="font-ui text-xs text-navy/80">{s.format}</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign size={17} className="text-teal shrink-0 mt-0.5" />
                  <div><p className="font-ui text-[11px] text-navy/50 uppercase tracking-wide">Investment</p><p className="font-ui text-xs text-navy/80">{s.price}</p></div>
                </div>
              </div>

              <button onClick={() => setOpen(s.id)} className="mt-7 self-start inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 rounded-full font-ui font-semibold hover:bg-orange transition-colors group">
                Inquire <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        ))}
      </section>

      {/* Speaking cross-link */}
      <section className="max-w-5xl mx-auto px-5 py-8">
        <div className="bg-navy text-cream rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div>
            <h3 className="font-serif-display text-2xl md:text-3xl font-bold">Booking a keynote or corporate workshop?</h3>
            <p className="font-ui text-white/75 mt-2">Real leadership strategy, zero corporate fluff — for stages, retreats, and boardrooms.</p>
          </div>
          <Link to="/speaking" className="shrink-0 inline-flex items-center gap-2 bg-teal text-navy px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
            See Speaking <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Inquiry modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-navy/60 backdrop-blur-sm" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif-display text-2xl font-bold text-navy">Inquire: {services.find((s) => s.id === open)?.title}</h3>
            <p className="font-ui text-sm text-navy/60 mt-1">Tell us a little about your business and we'll be in touch.</p>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal" style={{ borderColor: "var(--cream-dark)" }} />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal" style={{ borderColor: "var(--cream-dark)" }} />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What's going on in your business?" rows={3} className="w-full px-4 py-3 rounded-lg border font-ui text-sm outline-none focus:ring-2 ring-teal resize-none" style={{ borderColor: "var(--cream-dark)" }} />
              <div className="flex gap-3">
                <button type="button" onClick={() => setOpen(null)} className="flex-1 py-3 rounded-full border font-ui font-medium text-navy hover:bg-cream transition-colors" style={{ borderColor: "var(--cream-dark)" }}>Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-full bg-navy text-cream font-ui font-semibold hover:bg-orange transition-colors flex items-center justify-center gap-2"><Check size={17} /> Send</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
